/**
 * CARD SETUP — the pure model behind the SUNMI card-setup screens in Super Admin.
 * Spec: docs/agenticpos-rubytill/sunmi-card-setup.md (owner-approved 2026-10-14).
 *
 *   Reader capabilities  — what a reader can do, by catalog capability, never by vendor name
 *   Card setup policy    — what a device family (catalog `pos_terminal` row) allows
 *   Register cardSetup   — the primary + backup readers this register uses
 *
 * EVERYTHING HERE IS GATED on `isCardSetupModel(row)` by the callers. A register
 * whose catalog model carries no `cardSetupPolicy` never reaches this module, so
 * every other till renders and saves exactly as before.
 *
 * Shared-types first: each helper uses the `@platform/shared-types` runtime
 * export when the build has it (guarded at module scope — a stale pre-bundle
 * resolves it undefined) and the identical local rule otherwise.
 * TODO(sunmi-backend): drop the local mirrors once `cardReaders.ts` ships in
 * every shared-types build the console is bundled against.
 */
import * as SharedTypes from '@platform/shared-types';

const ST = SharedTypes as Record<string, unknown>;
const fn = <T extends (...a: any[]) => any>(name: string): T | null =>
  (typeof ST?.[name] === 'function' ? (ST[name] as T) : null);

// ---------------------------------------------------------------------------
// Reader capabilities
// ---------------------------------------------------------------------------

export const READER_KINDS = ['tap_to_pay', 'dejavoo_p', 'magtek_ped', 'magtek_go', 'sunbay_tapro'] as const;
export type ReaderKind = (typeof READER_KINDS)[number];

export interface ReaderCapabilities {
  credit: boolean; pinDebit: boolean;
  tap: boolean; dip: boolean; swipe: boolean;
  screen: boolean; tipOnScreen: boolean; signatureOnScreen: boolean;
}

export const CAPABILITY_FIELDS: ReadonlyArray<{ key: keyof ReaderCapabilities; label: string }> = [
  { key: 'credit', label: 'Credit' },
  { key: 'pinDebit', label: 'PIN debit' },
  { key: 'tap', label: 'Tap' },
  { key: 'dip', label: 'Dip' },
  { key: 'swipe', label: 'Swipe' },
  { key: 'screen', label: 'Screen' },
  { key: 'tipOnScreen', label: 'Tip on screen' },
  { key: 'signatureOnScreen', label: 'Signature on screen' },
];

const caps = (on: Array<keyof ReaderCapabilities>): ReaderCapabilities => {
  const out = { credit: false, pinDebit: false, tap: false, dip: false, swipe: false, screen: false, tipOnScreen: false, signatureOnScreen: false };
  for (const k of on) out[k] = true;
  return out;
};

const LOCAL_DEFAULT_CAPS: Record<ReaderKind, ReaderCapabilities> = {
  tap_to_pay: caps(['credit', 'tap', 'screen', 'tipOnScreen']),
  dejavoo_p: caps(['credit', 'pinDebit', 'tap', 'dip', 'swipe', 'screen', 'tipOnScreen', 'signatureOnScreen']),
  magtek_ped: caps(['credit', 'pinDebit', 'tap', 'dip', 'swipe', 'screen']),
  magtek_go: caps(['credit', 'tap', 'dip', 'swipe']),
  sunbay_tapro: caps(['credit', 'pinDebit', 'tap', 'dip', 'swipe', 'screen', 'tipOnScreen', 'signatureOnScreen']),
};

const isCapsObject = (v: unknown): v is ReaderCapabilities =>
  !!v && typeof v === 'object' && CAPABILITY_FIELDS.every(({ key }) => typeof (v as any)[key] === 'boolean');

const sharedDefaults = ST?.DEFAULT_READER_CAPABILITIES as Record<string, unknown> | undefined;
export const DEFAULT_READER_CAPABILITIES: Record<ReaderKind, ReaderCapabilities> =
  sharedDefaults && READER_KINDS.every((k) => isCapsObject(sharedDefaults[k]))
    ? (sharedDefaults as Record<ReaderKind, ReaderCapabilities>)
    : LOCAL_DEFAULT_CAPS;

const sharedKindOf = fn<(d: unknown) => ReaderKind | null>('readerKindOf');
const sharedCapsOf = fn<(d: unknown, row?: unknown) => ReaderCapabilities | null>('capabilitiesOf');
const sharedIsCardSetupModel = fn<(row: unknown) => boolean>('isCardSetupModel');
const sharedPolicyOf = fn<(row: unknown) => CardSetupPolicy | null>('cardSetupPolicyOf');

const P_CLASSES = ['dejavoo_spin_usb', 'dejavoo_spin_cloud'];
const RTPAY_CLASS = 'dejavoo_rubytill_pay';

/** The engine on a RubyTill Pay device, wherever the row carries it. */
const rtpayEngine = (d: any): string | null =>
  d?.rtpay?.cardEngine || d?.rtpayCardEngine || (d?.model === 'Android Tap to Pay' ? 'tap_to_pay' : null);

/**
 * The location list endpoint carries the engine as `rtpayCardEngine`; the
 * shared helper reads `rtpay.cardEngine`. Fold one into the other first, or a
 * Tap to Pay device from the list would read as a P-series reader.
 */
const normalized = (d: any) => {
  if (!d || typeof d !== 'object') return d;
  const engine = rtpayEngine(d);
  return engine && d?.rtpay?.cardEngine !== engine ? { ...d, rtpay: { ...(d.rtpay || {}), cardEngine: engine } } : d;
};

/** MagTek DynaFlex II rows: one class, the model decides the kind (mirror of shared-types MAGTEK_READER_MODELS). */
export const MAGTEK_CLASS = 'magtek_usb';
export const MAGTEK_KIND_BY_MODEL: Record<string, 'magtek_ped' | 'magtek_go'> = {
  'MagTek DynaFlex II PED': 'magtek_ped',
  'MagTek DynaFlex II Go': 'magtek_go',
};

/** A device row → its reader family (a kind = a lane family). Unknown ⇒ null. */
export function readerKindOf(device: any): ReaderKind | null {
  if (sharedKindOf) return sharedKindOf(normalized(device));
  if (isKind(device?.readerKind)) return device.readerKind;
  const cls = String(device?.deviceClass || '');
  if (cls === RTPAY_CLASS) { const e = rtpayEngine(device); return e === 'tap_to_pay' ? 'tap_to_pay' : e === 'sunbay' ? 'sunbay_tapro' : 'dejavoo_p'; }
  if (P_CLASSES.includes(cls)) return 'dejavoo_p';
  if (cls === MAGTEK_CLASS) return MAGTEK_KIND_BY_MODEL[String(device?.model || '')] ?? null;
  if (cls === 'sunbay_tapro') return device?.model === 'SUNMI CPad Pay' ? 'sunbay_tapro' : null;
  return null;
}

/** What this device can do — the catalog row's `readerCapabilities` overrides the kind default. */
export function capabilitiesOf(device: any, catalogRow?: { readerCapabilities?: unknown } | null): ReaderCapabilities | null {
  if (sharedCapsOf) return sharedCapsOf(normalized(device), catalogRow ?? undefined);
  const kind = readerKindOf(device);
  if (!kind) return null;
  // The catalog override is PARTIAL — laid over the kind default key by key.
  const out = { ...DEFAULT_READER_CAPABILITIES[kind] };
  const o = catalogRow?.readerCapabilities as Record<string, unknown> | null | undefined;
  if (o && typeof o === 'object') for (const { key } of CAPABILITY_FIELDS) if (typeof o[key] === 'boolean') out[key] = o[key] as boolean;
  return out;
}

/** "credit + PIN debit" — the two facts the payment screens care about. */
export function capabilitySummary(c: ReaderCapabilities | null | undefined): string {
  if (!c) return 'unknown';
  const parts = [c.credit ? 'credit' : null, c.pinDebit ? 'PIN debit' : null].filter(Boolean);
  return parts.length ? parts.join(' + ') : 'no card types';
}

/** Super Admin label for a reader family. Super Admin may name vendors (owner rule 4). */
export const KIND_LABEL: Record<ReaderKind, string> = {
  tap_to_pay: 'Android Tap to Pay',
  dejavoo_p: 'Dejavoo P17',
  magtek_ped: 'MagTek DynaFlex II PED',
  magtek_go: 'MagTek DynaFlex II Go',
  sunbay_tapro: 'RubyTill Pay CPad',
};
export const kindLabel = (k: string | null | undefined) => (k && (KIND_LABEL as Record<string, string>)[k]) || 'Card reader';

// ---------------------------------------------------------------------------
// Card setup policy (catalog `pos_terminal` rows)
// ---------------------------------------------------------------------------

export interface PolicyReader { kind: ReaderKind; primary: boolean; backup: boolean; enabled: boolean }
export interface CardSetupPolicy {
  profile: 'sunmi_t3';
  variant: 'single' | 'dual';
  customerDisplay: boolean;
  readers: PolicyReader[];
  defaultPrimary: ReaderKind;
  defaultBackup: ReaderKind | 'none';
  allowNoBackup: boolean;
}

const isKind = (v: unknown): v is ReaderKind => typeof v === 'string' && (READER_KINDS as readonly string[]).includes(v);

/** A structurally valid policy, or null. Never throws. */
export function policyOf(row: any): CardSetupPolicy | null {
  if (sharedPolicyOf) return sharedPolicyOf(row);
  const p = row?.cardSetupPolicy;
  if (!p || typeof p !== 'object') return null;
  if (typeof p.profile !== 'string' || !p.profile) return null;
  if (p.variant !== 'single' && p.variant !== 'dual') return null;
  if (!Array.isArray(p.readers) || !p.readers.length) return null;
  if (!p.readers.every((r: any) => isKind(r?.kind) && typeof r.primary === 'boolean' && typeof r.backup === 'boolean' && typeof r.enabled === 'boolean')) return null;
  if (!isKind(p.defaultPrimary)) return null;
  if (p.defaultBackup !== 'none' && !isKind(p.defaultBackup)) return null;
  return {
    profile: p.profile, variant: p.variant, customerDisplay: p.customerDisplay === true,
    readers: p.readers.map((r: any) => ({ kind: r.kind, primary: r.primary, backup: r.backup, enabled: r.enabled })),
    defaultPrimary: p.defaultPrimary, defaultBackup: p.defaultBackup, allowNoBackup: p.allowNoBackup !== false,
  };
}

/** THE GATE. A catalog row with a valid card setup policy — nothing else engages the new paths. */
export function isCardSetupModel(row: unknown): boolean {
  if (!row) return false;
  if (sharedIsCardSetupModel) return sharedIsCardSetupModel(row);
  return policyOf(row) !== null;
}

/** Reader kinds the policy allows in a role (enabled only). */
export const allowedKinds = (p: CardSetupPolicy, role: Role): ReaderKind[] =>
  p.readers.filter((r) => r.enabled && r[role]).map((r) => r.kind);

/** Same as allowedKinds but including "coming next" rows, for display. */
export const listedKinds = (p: CardSetupPolicy, role: Role): PolicyReader[] => p.readers.filter((r) => r[role]);

/** A blank policy for a catalog row being given one. */
export const blankPolicy = (variant: 'single' | 'dual' = 'dual'): CardSetupPolicy => ({
  profile: 'sunmi_t3', variant, customerDisplay: variant === 'dual',
  readers: [
    { kind: 'tap_to_pay', primary: true, backup: true, enabled: true },
    { kind: 'dejavoo_p', primary: true, backup: true, enabled: true },
    { kind: 'magtek_go', primary: true, backup: true, enabled: false },
  ],
  defaultPrimary: variant === 'dual' ? 'tap_to_pay' : 'dejavoo_p',
  defaultBackup: variant === 'dual' ? 'dejavoo_p' : 'tap_to_pay',
  allowNoBackup: true,
});

/** Mark/unmark a kind for a role; the default for that role follows if it is no longer allowed. */
export function setPolicyRole(p: CardSetupPolicy, kind: ReaderKind, role: Role, on: boolean): CardSetupPolicy {
  const readers = p.readers.map((r) => (r.kind === kind ? { ...r, [role]: on } : r));
  const next = { ...p, readers };
  if (role === 'primary' && !on && p.defaultPrimary === kind) {
    next.defaultPrimary = readers.find((r) => r.enabled && r.primary)?.kind || p.defaultPrimary;
  }
  if (role === 'backup' && !on && p.defaultBackup === kind) next.defaultBackup = 'none';
  return next;
}

// ---------------------------------------------------------------------------
// Register cardSetup
// ---------------------------------------------------------------------------

export type Role = 'primary' | 'backup';
export type TipLocation = 'customer_display' | 'terminal' | 'off';

export interface CardSetup {
  profile: string;
  readers: Array<{ role: Role; emvDeviceId: string }>;
  tipLocation: TipLocation;
  showBackupAlways: boolean;
  offerBackupOnFail: boolean;
  startOnBackupWhenPrimaryOffline: boolean;
}

export interface ResolvedReader {
  role: Role; emvDeviceId: string; kind: ReaderKind | null; deviceClass?: string;
  transport?: string; creditMethod?: string; deviceName?: string | null; neutralName?: string | null;
  capabilities: ReaderCapabilities | null;
}

const usable = (d: any) => d?.status !== 'inactive' && d?.status !== 'deactivated';

export const readerId = (s: CardSetup | null | undefined, role: Role): string | null =>
  s?.readers.find((r) => r.role === role)?.emvDeviceId || null;

/**
 * READ A STORED SETUP into the editable shape. Also reads the earlier dev-only
 * shape (`tapToPayEnabled/primary/tapToPayEmvDeviceId/readerEmvDeviceId`),
 * which never left dev — so the screen never shows a blank for a till that had one.
 */
export function readSetup(stored: any, policy: CardSetupPolicy): CardSetup | null {
  if (!stored || typeof stored !== 'object') return null;
  let readers: CardSetup['readers'] = [];
  if (Array.isArray(stored.readers)) {
    readers = stored.readers
      .filter((r: any) => (r?.role === 'primary' || r?.role === 'backup') && typeof r.emvDeviceId === 'string' && r.emvDeviceId)
      .map((r: any) => ({ role: r.role, emvDeviceId: r.emvDeviceId }));
  } else {
    const tap = stored.tapToPayEnabled !== false ? (stored.tapToPayEmvDeviceId || null) : null;
    const rdr = stored.readerEmvDeviceId || null;
    const tapFirst = stored.primary !== 'reader' && !!tap;
    const [p, b] = tapFirst ? [tap, rdr] : [rdr, tap];
    if (p) readers.push({ role: 'primary', emvDeviceId: p });
    if (b) readers.push({ role: 'backup', emvDeviceId: b });
  }
  const tip: TipLocation = stored.tipLocation === 'customer_display' || stored.tipLocation === 'terminal' || stored.tipLocation === 'off'
    ? stored.tipLocation : (policy.customerDisplay ? 'customer_display' : 'terminal');
  return {
    profile: typeof stored.profile === 'string' ? stored.profile : policy.profile,
    readers,
    tipLocation: tip,
    showBackupAlways: stored.showBackupAlways !== false,
    offerBackupOnFail: stored.offerBackupOnFail !== false,
    startOnBackupWhenPrimaryOffline: (stored.startOnBackupWhenPrimaryOffline ?? stored.startOnBackupWhenTapOffline) !== false,
  };
}

/** Devices at the location that fit a role under the policy (enabled kinds, usable, not bound to another till). */
export function devicesForRole(devices: any[], policy: CardSetupPolicy, role: Role, registerId: string): any[] {
  const kinds = allowedKinds(policy, role);
  return devices.filter((d) => {
    const k = readerKindOf(d);
    if (!k || !kinds.includes(k) || !usable(d)) return false;
    // A P-series reader is wired to one till; a Tap to Pay device lives on this till.
    return !d.registerId || d.registerId === registerId;
  });
}

/**
 * SMART DEFAULTS for a first setup: the policy's default primary/backup kinds,
 * each on the first fitting device at the location; tips at the customer
 * display if the model has one, else on the terminal (if that reader can), else off.
 */
export function defaultSetup(policy: CardSetupPolicy, devices: any[], registerId: string, catalog?: CatalogCaps): CardSetup {
  const pick = (kind: ReaderKind | 'none', role: Role, not?: string | null) => {
    if (kind === 'none') return null;
    return devicesForRole(devices, policy, role, registerId).find((d) => readerKindOf(d) === kind && d.deviceId !== not)?.deviceId || null;
  };
  const p = pick(policy.defaultPrimary, 'primary');
  const b = pick(policy.defaultBackup, 'backup', p);
  const readers: CardSetup['readers'] = [];
  if (p) readers.push({ role: 'primary', emvDeviceId: p });
  if (b) readers.push({ role: 'backup', emvDeviceId: b });
  const anyTip = readers.some((r) => capsFor(devices.find((d) => d.deviceId === r.emvDeviceId), catalog)?.tipOnScreen);
  return {
    profile: policy.profile,
    readers,
    tipLocation: policy.customerDisplay ? 'customer_display' : anyTip ? 'terminal' : 'off',
    showBackupAlways: true, offerBackupOnFail: true, startOnBackupWhenPrimaryOffline: true,
  };
}

/** Catalog payment-device rows, by modelId, for capability overrides. */
export type CatalogCaps = Record<string, { readerCapabilities?: unknown } | undefined>;
export const capsFor = (device: any, catalog?: CatalogCaps): ReaderCapabilities | null =>
  device ? capabilitiesOf(device, (device.modelId && catalog?.[device.modelId]) || null) : null;

/** Put a device in a role (null removes the role). The other role drops the same device. */
export function setRole(s: CardSetup, role: Role, id: string | null): CardSetup {
  const other: Role = role === 'primary' ? 'backup' : 'primary';
  const keep = s.readers.filter((r) => r.role === other && r.emvDeviceId !== id);
  const readers = id ? [...keep, { role, emvDeviceId: id }] : keep;
  readers.sort((a, b) => (a.role === 'primary' ? -1 : 1) - (b.role === 'primary' ? -1 : 1));
  return { ...s, readers };
}

/** Swap primary and backup. A lone primary stays where it is. */
export function swapRoles(s: CardSetup): CardSetup {
  const p = readerId(s, 'primary'); const b = readerId(s, 'backup');
  if (!p || !b) return s;
  return { ...s, readers: [{ role: 'primary', emvDeviceId: b }, { role: 'backup', emvDeviceId: p }] };
}

/** May the swap happen under the policy? */
export function canSwap(s: CardSetup, policy: CardSetupPolicy, devices: any[]): boolean {
  const p = devices.find((d) => d.deviceId === readerId(s, 'primary'));
  const b = devices.find((d) => d.deviceId === readerId(s, 'backup'));
  if (!p || !b) return false;
  const pk = readerKindOf(p); const bk = readerKindOf(b);
  return !!pk && !!bk && allowedKinds(policy, 'primary').includes(bk) && allowedKinds(policy, 'backup').includes(pk);
}

/** Resolve the setup's readers against the device pool. */
export function resolveReaders(s: CardSetup, devices: any[], catalog?: CatalogCaps): ResolvedReader[] {
  return s.readers.map((r) => {
    const d = devices.find((x) => x.deviceId === r.emvDeviceId) || null;
    return {
      role: r.role, emvDeviceId: r.emvDeviceId, kind: d ? readerKindOf(d) : null,
      deviceClass: d?.deviceClass, deviceName: d?.deviceName ?? null, capabilities: capsFor(d, catalog),
    };
  }).sort((a, b) => (a.role === 'primary' ? -1 : 1) - (b.role === 'primary' ? -1 : 1));
}

/** The server's `cardSetupResolved.readers` when it is the new shape, else null. */
export function serverResolved(r: any): ResolvedReader[] | null {
  if (!r || !Array.isArray(r.readers)) return null;
  return r.readers
    .filter((x: any) => x && (x.role === 'primary' || x.role === 'backup') && x.emvDeviceId)
    .map((x: any) => ({
      role: x.role, emvDeviceId: x.emvDeviceId, kind: isKind(x.kind) ? x.kind : null,
      deviceClass: x.deviceClass, transport: x.transport, creditMethod: x.creditMethod,
      deviceName: x.deviceName ?? null, neutralName: x.neutralName ?? null,
      capabilities: isCapsObject(x.capabilities) ? x.capabilities : (isKind(x.kind) ? DEFAULT_READER_CAPABILITIES[x.kind] : null),
    }))
    .sort((a: ResolvedReader, b: ResolvedReader) => (a.role === 'primary' ? -1 : 1) - (b.role === 'primary' ? -1 : 1));
}

/** First reader (primary first) that takes PIN debit — where debit goes. */
export const debitReaderOf = (readers: ResolvedReader[]): ResolvedReader | null =>
  readers.find((r) => r.capabilities?.pinDebit) || null;

export const readerTitle = (r: Pick<ResolvedReader, 'kind' | 'deviceName'>) => kindLabel(r.kind);

/**
 * PAYMENT-OPTION LOCKS — computed, shown with reasons; stored fields unchanged.
 *   debit possible ⇔ some reader takes PIN debit; surcharge ⇒ debit ⇒ surcharge possible ⇔ debit possible.
 */
export interface PaymentLocks {
  debitPossible: boolean;
  debitReader: ResolvedReader | null;
  /** Plain reason, set only when debit (and so surcharge) is locked off. */
  reason: string | null;
}
export function paymentLocks(readers: ResolvedReader[]): PaymentLocks {
  const debitReader = debitReaderOf(readers);
  return {
    debitPossible: !!debitReader,
    debitReader,
    reason: debitReader ? null : (readers.length ? 'No reader on this register can take PIN' : 'No card reader yet — add a card reader on the Terminal (EMV/SoftPOS) tab'),
  };
}

/** The tip-location choices: Customer display (hidden without one), Terminal, Off. */
export function tipLocationOptions(policy: CardSetupPolicy, readers: ResolvedReader[]): Array<{ id: TipLocation; label: string; disabled?: boolean; reason?: string }> {
  const out: Array<{ id: TipLocation; label: string; disabled?: boolean; reason?: string }> = [];
  if (policy.customerDisplay) out.push({ id: 'customer_display', label: 'Customer display' });
  const terminalOk = readers.some((r) => r.capabilities?.tipOnScreen);
  out.push({ id: 'terminal', label: 'Terminal (EMV/SoftPOS)', disabled: !terminalOk, reason: terminalOk ? undefined : 'No reader on this register can ask for a tip on its screen' });
  out.push({ id: 'off', label: 'Off' });
  return out;
}

/** The invariants the server enforces — a non-empty list disables Save. */
export function setupProblems(s: CardSetup, policy: CardSetupPolicy, devices: any[], catalog?: CatalogCaps): string[] {
  const out: string[] = [];
  const prim = s.readers.filter((r) => r.role === 'primary');
  const back = s.readers.filter((r) => r.role === 'backup');
  if (prim.length !== 1) out.push('Pick the primary reader.');
  if (back.length > 1) out.push('Only one backup reader.');
  if (!back.length && !policy.allowNoBackup) out.push('This model needs a backup reader.');
  if (prim[0] && back[0] && prim[0].emvDeviceId === back[0].emvDeviceId) out.push('Primary and backup must be different devices.');
  for (const r of s.readers) {
    const d = devices.find((x) => x.deviceId === r.emvDeviceId);
    const k = d ? readerKindOf(d) : null;
    if (d && (!k || !allowedKinds(policy, r.role).includes(k))) out.push(`${kindLabel(k)} is not allowed as the ${r.role} on this model.`);
  }
  if (s.tipLocation === 'customer_display' && !policy.customerDisplay) out.push('This model has no customer display for tips.');
  if (s.tipLocation === 'terminal' && !resolveReaders(s, devices, catalog).some((r) => r.capabilities?.tipOnScreen)) {
    out.push('No reader here can ask for a tip on its screen — pick another tip location.');
  }
  return out;
}

export const sameSetup = (a: CardSetup | null, b: CardSetup | null) => JSON.stringify(a) === JSON.stringify(b);

/** "••••4471" — never the full TPN / merchant code on screen. */
export const masked = (v: unknown) => {
  const s = String(v || '').trim();
  return s ? `••••${s.slice(-4)}` : '—';
};

/**
 * A device was just registered: put it in the EMPTY slot it fits — primary
 * first, else backup — in the unsaved draft. A device already in the setup,
 * or one the policy does not allow in the empty role, leaves the draft as is.
 */
export function autoFill(s: CardSetup, policy: CardSetupPolicy, device: any): CardSetup {
  if (!device?.deviceId || s.readers.some((r) => r.emvDeviceId === device.deviceId)) return s;
  const kind = readerKindOf(device);
  if (!kind) return s;
  for (const role of ['primary', 'backup'] as Role[]) {
    if (!readerId(s, role) && allowedKinds(policy, role).includes(kind)) return setRole(s, role, device.deviceId);
  }
  return s;
}

/** Unassigned readers the policy allows, registered at another of the tenant's locations. */
export function otherLocationReaders(devices: any[], policy: CardSetupPolicy): any[] {
  const kinds = new Set([...allowedKinds(policy, 'primary'), ...allowedKinds(policy, 'backup')]);
  return devices.filter((d) => {
    const k = readerKindOf(d);
    return !!k && kinds.has(k) && usable(d) && !d.registerId;
  });
}
